import * as Schema from "effect/Schema";

/**
 * The Computer Use Service is the one bundled computer-use process an
 * Environment runs, owned by the Environment Service and started and stopped
 * from Settings.
 *
 * It is not an MCP server AFAR hands to a provider. Agents reach it as tools
 * on AFAR's own MCP server, so nothing is written into a provider's native
 * configuration and nothing is fetched or installed on the host. This name is
 * what the Settings row and the readiness checklist call it.
 */
export const COMPUTER_USE_SERVICE_NAME = "afar-computer-use";

/** The App Control Tool Surface's own MCP server, the one that carries the `preview_*` tools. */
export const APP_CONTROL_MCP_SERVER_NAME = "afar-research-app";

/**
 * System Settings panes the Environment may deep-link on its own host. A
 * closed set: the host opens these outside the http/https allowlist that
 * guards every other external link, so a client names a pane, never a URL.
 */
export const MacosPrivacyPane = Schema.Literals(["accessibility", "screenRecording"]);
export type MacosPrivacyPane = typeof MacosPrivacyPane.Type;

export const ComputerUseServiceState = Schema.Literals([
  "stopped",
  "starting",
  /**
   * Replacing the supervised process so a macOS grant reaches it. Its own
   * state because macOS hands these permissions to a process when it starts,
   * which makes a restart a routine step in granting rather than a failure or
   * a first run. Agents are held off for its duration: there is one process,
   * and it is this one.
   */
  "restarting",
  "running",
  "failed",
]);
export type ComputerUseServiceState = typeof ComputerUseServiceState.Type;

/**
 * What the agent must be able to do before computer use is usable. Each one is
 * decided by actually calling the tool that certifies it — the grant belongs
 * to the binary behind it, so nothing short of trying answers honestly.
 */
export const ComputerUseReadinessCheckId = Schema.Literals(["service", "accessibility", "screen"]);
export type ComputerUseReadinessCheckId = typeof ComputerUseReadinessCheckId.Type;

export const ComputerUseReadinessCheck = Schema.Struct({
  id: ComputerUseReadinessCheckId,
  /** `unknown` means the probe could not run, never "assume granted". */
  state: Schema.Literals(["satisfied", "blocked", "unknown"]),
  /** The server's own words where it gave them, so the fix is the real one. */
  detail: Schema.optionalKey(Schema.String),
});
export type ComputerUseReadinessCheck = typeof ComputerUseReadinessCheck.Type;

export const ComputerUseServiceStatus = Schema.Struct({
  /** What the supervisor actually achieved, which can lag or contradict `enabled`. */
  state: ComputerUseServiceState,
  /** Effective availability: a host's saved setting, or a Cloud Workspace's bundled service. */
  enabled: Schema.Boolean,
  /** Cloud Workspace services start on first use and have no user switch. */
  onDemand: Schema.optionalKey(Schema.Boolean),
  /** False where this Application Release staged no computer-use build for this host. */
  supported: Schema.Boolean,
  /** Tools the running process advertised, so the UI can show the service is really answering. */
  toolCount: Schema.optionalKey(Schema.Int),
  /** Why the service is stopped or failed, phrased for the user. */
  detail: Schema.optionalKey(Schema.String),
  /** Empty until the service runs: nothing can be probed through a process that is not up. */
  checks: Schema.Array(ComputerUseReadinessCheck),
  /**
   * True while the Environment is resolving what macOS allows: raising the
   * prompts, restarting the server so a grant reaches it, or running the
   * probes. It spans the whole cycle so every client's row reads busy, not
   * only the one that asked.
   */
  checking: Schema.Boolean,
  /**
   * True where this Environment's host can open the System Settings panes
   * these checks name. Offering the link anywhere else is a button that
   * cannot work.
   */
  opensPrivacySettings: Schema.Boolean,
  /**
   * A development build is not code-signed and is launched directly rather
   * than through LaunchServices, so macOS ties the grant to that exact copy
   * of the binary. Rebuilding it silently invalidates the grant while System
   * Settings still lists an app of the same name as allowed.
   */
  isDevelopmentBuild: Schema.Boolean,
});
export type ComputerUseServiceStatus = typeof ComputerUseServiceStatus.Type;

export class ComputerUseServiceError extends Schema.TaggedErrorClass<ComputerUseServiceError>()(
  "ComputerUseServiceError",
  { message: Schema.String },
) {}
